/**
 * Parses CSS box and text shadows.
 */

import * as CssParser from './CssParser';
import * as CssUtils from '@packages/systems/style-legacy/utils';
import * as StringUtils from '@packages/utilities/string-utils';
import {splitCssValue} from '@packages/systems/core/utils/CssStringUtils';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';

/**
 * Parses a shadow chunk of styles into a JSON list of parts.f
 * @param  {Object} styleData The original values from StyleUtils.buildStyleMap.
 * @return {Object}           Parsed shadow information.
 */
export function parseParts(styleData, variableMap) {
  return CssParser.parseSimpleParts(styleData, ',', parseItem(variableMap));
}

/**
 * Parses an individual shadow's CSS and populates the JSON object.
 * @param  {String} cssValue     The CSS value for this shadow.
 * @param  {String} mode         The upstream/local/browser mode we're currently parsing.
 * @param  {Object} partJson     The JSON object for this part from the parseParts() method.
 * @param  {Object} opts         { omitDefaultUnit: If true, will not pass "px" as the default unit when parsing values }
 * @return {Object}
 */
export function parseItem(variableMap) {
  return function parseItem_(cssValue, mode, partJson, opts) {
    opts = opts || {};
    partJson = partJson || {};
    partJson[mode] = partJson[mode] || {};

    if (StringUtils.isEmpty(cssValue)) {
      return {};
    }

    const chunks = splitCssValue(cssValue, ' ');
    const defaultUnit = opts.omitDefaultUnit ? undefined : 'px';

    // If there is no value, don't set any properties
    if (chunks.length === 1 && chunks[0] === 'none') {
      return partJson;
    }

    const part = partJson[mode];

    // Always extract inset keyword first (since it can be first or last)
    if (CssUtils.extractValue(chunks, 'inset') === 'inset') {
      part.inset = true;
    }

    // Extract color next - depending on browser (if value came from computed CSS), it could be first or last
    part.color = CssUtils.toWebflowCssValueObject(
      CssUtils.extractColor(chunks, variableMap) || 'black'
    );

    const [xChunk, yChunk, blurChunk, spreadChunk] = chunks;

    if (StyleVariables.isVariablePlaceholder(xChunk)) {
      part.x = {value: xChunk, raw: xChunk, unit: null};
    } else {
      part.x = CssUtils.parseValue(xChunk, 0, defaultUnit);
      part.x.raw = `${part.x.value}${part.x.unit}`;
    }

    if (StyleVariables.isVariablePlaceholder(yChunk)) {
      part.y = {value: yChunk, raw: yChunk, unit: null};
    } else {
      part.y = CssUtils.parseValue(yChunk, 0, defaultUnit);
      part.y.raw = `${part.y.value}${part.y.unit}`;
    }

    if (StyleVariables.isVariablePlaceholder(blurChunk)) {
      part.blur = {value: blurChunk, raw: blurChunk, unit: null};
    } else {
      part.blur = CssUtils.parseValue(blurChunk, 0, defaultUnit);
      part.blur.raw = `${part.blur.value}${part.blur.unit}`;
    }

    if (StyleVariables.isVariablePlaceholder(spreadChunk)) {
      part.spread = {value: spreadChunk, raw: spreadChunk, unit: null};
    } else {
      part.spread = CssUtils.parseValue(spreadChunk, 0, defaultUnit);
      part.spread.raw = `${part.spread.value}${part.spread.unit}`;
    }

    return partJson;
  };
}
